import { api } from './api';

export type PlanStatus = 'generating' | 'ready' | 'partially_scheduled' | 'scheduled' | 'dismissed' | 'failed';
export type PlanItemStatus = 'proposed' | 'accepted' | 'edited' | 'skipped' | 'scheduled' | 'failed';
export type PlanSourceType = 'customer_question' | 'occasion' | 'repeat_winner' | 'slot' | 'pillar';
export type PlanFailure = 'NO_CHANNELS' | 'NOTHING_TO_PLAN' | 'AI_UNAVAILABLE' | 'AI_ANSWER_UNUSABLE' | 'LIMIT_REACHED' | 'UNKNOWN';

/** One proposed post of a weekly plan (Task 2.2). */
export interface PlanItem {
  id: string;
  position: number;
  sourceType: PlanSourceType;
  sourceRef: string | null;
  sourceLabel: string;
  sourceCount: number | null;
  suggestedAt: string;
  platforms: string[];
  /** { [PLATFORM]: caption } */
  captions: Record<string, string>;
  mediaKeys: string[];
  posterJobId: string | null;
  status: PlanItemStatus;
  postId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface WeeklyPlan {
  id: string;
  /** Monday, brand time (YYYY-MM-DD). */
  weekStart: string;
  status: PlanStatus;
  generatedAt: string | null;
  headline: string | null;
  failureReason: PlanFailure | null;
  items: PlanItem[];
}

export interface PlanSettings {
  planDay: number;
  planTime: string;
  postsPerWeek: 3 | 5 | 7;
  planChannels: string[];
  autoPoster: boolean;
}

export interface PlanItemPatch {
  suggestedAt?: string;
  platforms?: string[];
  captions?: Record<string, string>;
  mediaKeys?: string[];
}

/** /api/v1/plans (docs/features/weekly-plan.md). */
export const plansService = {
  async current(): Promise<{ enabled: boolean; plan: WeeklyPlan | null }> {
    return (await api.get<{ enabled: boolean; plan: WeeklyPlan | null }>('/plans/current')).data;
  },
  async generate(): Promise<WeeklyPlan> {
    return (await api.post<WeeklyPlan>('/plans/generate')).data;
  },
  async regenerate(planId: string): Promise<WeeklyPlan> {
    return (await api.post<WeeklyPlan>(`/plans/${planId}/regenerate`)).data;
  },
  async acceptAll(planId: string): Promise<{ plan: WeeklyPlan; failed: Array<{ itemId: string; code: string }> }> {
    return (await api.post<{ plan: WeeklyPlan; failed: Array<{ itemId: string; code: string }> }>(`/plans/${planId}/accept-all`)).data;
  },
  async addFromTopic(planId: string, topicId: string): Promise<PlanItem> {
    return (await api.post<PlanItem>(`/plans/${planId}/items`, { topicId })).data;
  },
  async itemAction(planId: string, itemId: string, action: 'accept' | 'undo' | 'skip' | 'regenerate'): Promise<PlanItem> {
    return (await api.post<PlanItem>(`/plans/${planId}/items/${itemId}/${action}`)).data;
  },
  async patchItem(planId: string, itemId: string, patch: PlanItemPatch): Promise<PlanItem> {
    return (await api.patch<PlanItem>(`/plans/${planId}/items/${itemId}`, patch)).data;
  },
  async link(planId: string, itemId: string, postId: string): Promise<PlanItem> {
    return (await api.post<PlanItem>(`/plans/${planId}/items/${itemId}/link`, { postId })).data;
  },
  async settings(): Promise<PlanSettings> {
    return (await api.get<PlanSettings>('/plans/settings')).data;
  },
  async saveSettings(patch: Partial<PlanSettings>): Promise<PlanSettings> {
    return (await api.put<PlanSettings>('/plans/settings', patch)).data;
  },
};
